/**
 * Canonical breadcrumb crumbs for navigable destinations. For the same
 * provider, labels match the sidenav in `app/components/layout/sidebar/hooks.ts`
 * (locked by breadcrumbs.test.ts); a page for one entity may pin its provider.
 *
 * `getAdminSectionCrumb` resolves a pathname to its section's crumb: a
 * constant here, or a provider factory from `app/utils/providerBreadcrumbs.ts`.
 */

import { type CloudProvider } from "@stacklet/ui";

import { paths } from "app/constants/paths";
import { getAccountsBreadcrumb } from "app/utils/providerBreadcrumbs";

/** A crumb that is always clickable — `target` is mandatory. */
export interface BreadcrumbLink {
  label: string;
  target: string;
}

// Top-level sidenav destinations.
export const RESOURCES_BREADCRUMB: BreadcrumbLink = {
  label: "Resources",
  target: paths.resources,
};
export const BINDINGS_BREADCRUMB: BreadcrumbLink = {
  label: "Bindings",
  target: paths.adminBindings,
};
export const POLICIES_BREADCRUMB: BreadcrumbLink = {
  label: "Policies",
  target: paths.adminPolicies,
};
export const SETTINGS_BREADCRUMB: BreadcrumbLink = {
  label: "Settings",
  target: paths.users,
};
export const NOTIFICATIONS_BREADCRUMB: BreadcrumbLink = {
  label: "Notifications",
  target: paths.notificationsOverview,
};

// Query-param tab views used as parent crumbs by their child pages.
export const POLICY_COLLECTIONS_BREADCRUMB: BreadcrumbLink = {
  label: "Policy Collections",
  target: `${paths.adminPolicies}?tab=policy-collections`,
};
export const POLICY_REPOSITORIES_BREADCRUMB: BreadcrumbLink = {
  label: "Policy Repositories",
  target: `${paths.adminPolicies}?tab=repositories`,
};

// Mirrors the sidenav targets + activePaths in sidebar/hooks.ts: every
// /admin/* URL belongs to one of the top-level Accounts/Bindings/Policies
// items ("Admin" itself is not a sidenav concept and never appears in trails).
const ADMIN_SECTIONS: {
  crumb: (provider: CloudProvider) => BreadcrumbLink;
  pathPrefixes: string[];
}[] = [
  {
    crumb: getAccountsBreadcrumb,
    pathPrefixes: [paths.adminAccounts, paths.adminAccountGroups],
  },
  { crumb: () => BINDINGS_BREADCRUMB, pathPrefixes: [paths.adminBindings] },
  {
    crumb: () => POLICIES_BREADCRUMB,
    pathPrefixes: [
      paths.adminPolicies,
      paths.adminPolicyCollections,
      paths.adminRepositories,
    ],
  },
];

/** Root crumb for a page under /admin/* — the sidenav item that owns the path. */
export function getAdminSectionCrumb(
  pathname: string,
  provider: CloudProvider,
): BreadcrumbLink | undefined {
  return ADMIN_SECTIONS.find((section) =>
    section.pathPrefixes.some((prefix) => pathname.startsWith(prefix)),
  )?.crumb(provider);
}
